import IdeaProperty, {alwaysComputed, computingNow} from "@/types/IdeaProperty"
import type Instance from "@/types/Instance"
import {isEmpty} from "@/utils/propertyDisplay"
import {isRequired} from "@/utils/ontosUtils"
import {urlHref} from "@/utils/utils"
import {asArray} from "@/utils/arrayUtils"
import {t} from "@/i18n"

/**
 * As validações de CAMPO de um item: o que quem preenche corrige no próprio campo.
 *
 * **O par do servidor** (`lunedi/src/domain/fieldValidation.ts`), e a cópia é de propósito (decisão
 * do usuário): o que dá para conferir aqui é conferido aqui, na hora, sem ida ao servidor — e o
 * servidor confere de novo ao gravar, que é a garantia. Mudou lá, muda aqui.
 *
 * Só fica de fora o que o cliente não sabe sozinho, e vem da prévia do servidor, na mesma forma,
 * para a tela tratar as duas origens igual: o único, que depende dos outros itens, e as regras do
 * designer, que só o servidor avalia.
 *
 * Fora de qualquer tela: o formulário confere ao digitar, e quem mais gravar pode conferir antes.
 */
export type FieldFailure = {
    /** O alias do campo que falhou. */
    property: string
    validation: 'required' | 'url' | 'min-items' | 'max-items' | 'unique' | 'rule' | 'option' | 'not-member' | 'not-visible'
        | 'computed' | 'tree-cycle'
    /**
     * O que a frase precisa: a contagem do mínimo/máximo, o valor repetido e de quem é, a mensagem
     * que o designer escreveu na regra.
     */
    params?: Record<string, any>
    /**
     * O item do OUTRO lado, quando a falha é dele (o lado B), e `via`: o campo do item editado por
     * onde a gravação chegou lá — é embaixo dele que o formulário mostra a falha.
     */
    item?: { idea: string, identifier: string, propertyName: string, via: string }
}

/**
 * O nome ou o alias de um ambiente, de uma coleção, de uma propriedade: obrigatório, e sem repetir
 * entre os irmãos (`siblings`, onde `prop` é comparado). `current` é o alias do próprio em edição,
 * que não conta como repetido.
 *
 * Devolve a frase, ou vazio — a forma que o `:error-messages` espera. Era a mesma trinca
 * (`required`, `unique`, `validate`) escrita no formulário de ambiente, no de coleção e na base dos
 * de propriedade; só a lista de irmãos mudava.
 */
export function namingError(text: string | undefined, prop: string,
                            siblings: Array<Record<string, any>>, current?: string): string {
    if (!text) return t('required-message')
    const taken = siblings.some(sibling => sibling[prop] === text && !(current !== undefined && sibling.alias === current))
    return taken ? t('validation.already-used', {value: text}) : ''
}

/**
 * O que o item, como está GRAVADO, viola neste campo — as falhas que a leitura devolve quando a tela
 * as pede (`meta: failures`). É a marca do item antigo que uma regra nova, um obrigatório ou um único
 * pegou depois; ninguém é barrado por ela. A tela do item e a listagem perguntam por aqui.
 */
export function failuresAt(instance: Pick<Instance, '_failures'> | undefined, alias: string): FieldFailure[] {
    return ((instance && instance._failures) || []).filter(failure => failure.property === alias)
}

/**
 * O valor do campo mora no documento do item? Não na calculada de sempre, que a fórmula decide, nem no
 * reverso, que mora no outro lado — nenhum dos dois é validado como valor (o `stored` do servidor). O
 * "Calculado quando" mora: o preenchido é dele.
 */
export function isStored(property: IdeaProperty): boolean {
    return !alwaysComputed(property) && !(property.relationship && property.relationship.reverse)
}

/**
 * As falhas do valor de um campo. `instance` é o item em edição: o obrigatório pode depender de outros
 * campos — o "Obrigatório quando", que a prévia decide (`isRequired`).
 *
 * Obrigatório é do campo de um valor só; o múltiplo diz quantos pelo mínimo e máximo — é como o
 * design os oferece.
 *
 * No sim/não, obrigatório é ter escolhido um dos dois — e Não é escolha (decisão do usuário); quem
 * quer exigir Sim escreve uma regra. Ver o `BooleanInput`.
 */
export function valueFailures(property: IdeaProperty, value: any, instance: Record<string, any>): FieldFailure[] {
    //Nem o "Calculado quando" enquanto é calculado: o valor é da fórmula, e não de quem preenche
    if (!isStored(property) || computingNow(instance, property)) return []
    const failures: FieldFailure[] = []
    const fail = (validation: FieldFailure['validation'], params?: Record<string, any>) =>
        failures.push({property: property.alias, validation, ...(params ? {params} : {})})
    const values = asArray(value).filter(single => !isEmpty(single))

    if (property.multiple) {
        const {min, max} = property.itemCount || {} as Partial<NonNullable<IdeaProperty['itemCount']>>
        //Com o "Obrigatório quando", o mínimo só vale quando a condição dá Sim (`isRequired`)
        if (min && values.length < min && (!property.requiredWhen || isRequired(instance, property))) fail('min-items', {count: min})
        if (max && values.length > max) fail('max-items', {count: max})
    } else if (isRequired(instance, property) && !values.length) fail('required')

    if (property.type === 'text' && property.subtype === 'url' && values.some(single => !urlHref(single)))
        fail('url')
    return failures
}
